import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MultiSiteAwareComponent } from '~/infrastructure/multi-site-aware.component';
import { PageNames } from '~/models/pageNames';
import { PageState } from '~/shared/enums/stock-files.enum';
import { MaintenanceTypeDto, MaintenanceTypeRequestModel } from '~/models/modules/Asset/Transactions/Maintenance/maintenance.model';
import { ScheduleMaintenanceService } from '~/services/modules/assets/transactions/schedule-maintenance.service';
import { SharedService } from '~/services/sharedService';
import { ThemeManagerService } from '~/services/theme-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { LayoutManagerService } from '~/shared/components/layout/layout-manager';
import { SecurityService } from '~/services/security.service';

@Component({
    selector: 'asap-maintenance-type-upsert',
    templateUrl: './upsert.component.html',
    styleUrls: ['./upsert.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MaintenanceTypeUpsertComponent extends MultiSiteAwareComponent implements OnInit {
  @ViewChild('mainWindow') kendoMainWindow: ElementRef;

  _theme: string = 'enterprise';
  _active: boolean = false;
  _pageState: PageState = PageState.ADD;
  _page: string = PageNames.MaintenanceType;
  _permissionScope: string = PageNames.MaintenanceType;
  _currentUser: any;

  maintenanceTypeId: number | null = null;
  maintenanceTypeCode: string = '';
  maintenanceTypeName: string = '';

  isSaving: boolean = false;
  _model: MaintenanceTypeDto | null = null;

  constructor(
    private _scheduleService: ScheduleMaintenanceService,
    private _sharedService: SharedService,
    private _themeManager: ThemeManagerService,
    private _layoutComponent: LayoutComponent,
    private _layoutManager: LayoutManagerService,
    private _securityService: SecurityService
  ) {
    super();
  }

  ngOnInit(): void {
    this._currentUser = this._securityService.currentUser;
    this._themeManager.themeChange.subscribe((data) => {
      if (data) this._theme = data;
    });

    this.loadData();
  }

  canAccess(scope: string, operation: string): boolean {
    if (!scope) return true;
    return this._securityService.hasPermission(null, `${scope}-${operation}`);
  }

  loadData(): void {
    const incoming = this._sharedService.model;
    if (incoming && incoming.model) {
      this._model = incoming.model;
      this._pageState = incoming.pageState ?? PageState.ADD;
    } else if (incoming && (incoming.maintenanceTypeCode || incoming.id)) {
      this._model = incoming;
      this._pageState = incoming.pageState ?? PageState.EDIT;
    } else {
      this._model = null;
      this._pageState = incoming?.pageState ?? PageState.ADD;
    }
    this._active = true;

    if (this._model) {
      this.maintenanceTypeId = this._model.id || null;
      this.maintenanceTypeCode = this._model.maintenanceTypeCode || '';
      this.maintenanceTypeName = this._model.maintenanceTypeName || '';
    } else {
      this.maintenanceTypeId = null;
      this.maintenanceTypeCode = '';
      this.maintenanceTypeName = '';
    }
  }

  _onCancel(): void {
    this._closeForm(null);
  }

  _closeForm(resultModel: any = null): void {
    const closeComponent = {
      component: PageNames.MaintenanceType,
      isClosed: true,
      response: resultModel
    };
    this._active = false;
    this._layoutManager.removeOverlay();
    this._sharedService.closeDialog.emit(closeComponent);
    this._sharedService.model = {};
  }

  _edit(): void {
    this._pageState = PageState.EDIT;
  }

  _onStateChanges(e: any): void {
    const element: any = this.kendoMainWindow;
    if (!element?.el?.nativeElement) return;
    if (e === 'minimized') {
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    } else if (e === 'maximized') {
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    } else if (e === 'default') {
      element.el.nativeElement.classList.add('fullWidthFormForm');
    }
  }

  _onSave(action: 'save' | 'saveAndClose' | 'saveAndNew' | 'saveAndCopy'): void {
    if (!this.maintenanceTypeCode || !this.maintenanceTypeCode.trim()) {
      this._layoutComponent.openPopup({
        title: 'Validation',
        message: 'Please fill the Maintenance Type.',
        withoutConfirm: true
      });
      return;
    }

    const payload: MaintenanceTypeRequestModel = {
      maintenanceTypeCode: this.maintenanceTypeCode.trim(),
      maintenanceTypeName: this.maintenanceTypeName ? this.maintenanceTypeName.trim() : ''
    };

    this.isSaving = true;

    if (this._pageState === PageState.EDIT && this.maintenanceTypeId) {
      this._scheduleService.updateMaintenanceType(this.maintenanceTypeId, payload).subscribe({
        next: (res) => {
          this.isSaving = false;
          this.handleSaveSuccess(res, action);
        },
        error: (err) => {
          this.isSaving = false;
          const msg = err?.error?.data?.message || err?.error?.message || 'Error updating maintenance type.';
          this._layoutComponent.openPopup({
            title: 'Error',
            message: msg,
            withoutConfirm: true
          });
        }
      });
    } else {
      this._scheduleService.createMaintenanceType(payload).subscribe({
        next: (res) => {
          this.isSaving = false;
          this.handleSaveSuccess(res, action);
        },
        error: (err) => {
          this.isSaving = false;
          const msg = err?.error?.data?.message || err?.error?.message || 'Error creating maintenance type.';
          this._layoutComponent.openPopup({
            title: 'Error',
            message: msg,
            withoutConfirm: true
          });
        }
      });
    }
  }

  private handleSaveSuccess(savedModel: MaintenanceTypeDto, action: string): void {
    if (action === 'saveAndClose') {
      this._closeForm(savedModel);
    } else if (action === 'saveAndNew') {
      this.maintenanceTypeId = null;
      this.maintenanceTypeCode = '';
      this.maintenanceTypeName = '';
      this._model = null;
      this._pageState = PageState.ADD;
      this._layoutComponent.openPopup({
        title: 'Success',
        message: 'Maintenance Type saved successfully.',
        withoutConfirm: true
      });
    } else if (action === 'saveAndCopy') {
      this.maintenanceTypeId = null;
      this._pageState = PageState.ADD;
      this._layoutComponent.openPopup({
        title: 'Success',
        message: 'Maintenance Type saved. Ready for duplicate entry.',
        withoutConfirm: true
      });
    } else {
      // action === 'save'
      this._model = savedModel;
      this.maintenanceTypeId = savedModel.id;
      this._pageState = PageState.EDIT;
      this._layoutComponent.openPopup({
        title: 'Success',
        message: 'Maintenance Type saved successfully.',
        withoutConfirm: true
      });
    }
  }
}
